import { Button, Card, CardBody, Chip, Tabs, Tab, CardHeader, Divider } from "@nextui-org/react";
import { ArrowPathIcon, CheckIcon, EyeIcon, ListBulletIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import RootAdmin from "../../../components/layouts/RootAdmin";
import CountWidget from "../../../components/elements/CountWidget";
import { authStateFetcher, BASE_URL, getFetcher, postFetcher, sendInsw, sendInswPerubahan, patchFetcherWithoutId } from "../../../services/api";
import ReactSelect from "react-select";
import { gayaSelectDalamTabel, sasaranPortalMenu } from "../../../services/gayaSelectDalamTabel";
import useSWR from "swr";
import useCustomNavigate from "../../../hooks/useCustomNavigate";
import { useMemo, useState, useEffect, useCallback } from "react";
import { calculateRegistrasiRekomendasiProcessingDays, calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan, formattedDate, hasValidRole, isResponseErrorObject } from "../../../services/helpers";
import toast from "react-hot-toast";
import StatusPermohonanRegistrasi from "../../../enums/statusRegistrasi";
import useAuth from "../../../hooks/useAuth";
import RolesAccess from "../../../enums/roles";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";
import { endOfYear, format, startOfYear } from "date-fns";
import FormAddSK from "../../../components/fragments/admin/registration/FormAddSK";

export default function IndexPage() {
  const { user, roles } = useAuth();
  const [isLoadingPtpsp, setIsLoadingPtpsp] = useState(false);
  const isDirektur = hasValidRole(roles, [RolesAccess.DIREKTUR]);
  const canDelegasikan = hasValidRole(roles, [RolesAccess.KOORDINATOR, RolesAccess.SUPER_ADMIN]);
  // Koordinator (murni, tanpa role SuperAdmin/Direktur) hanya boleh mendelegasikan,
  // tidak perlu melihat/membuka detail data lewat kolom Aksi.
  const hideAksiForKoordinator = hasValidRole(roles, [RolesAccess.KOORDINATOR]) && !hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.DIREKTUR]);

  const { data: picRegistrasiData } = useSWR(canDelegasikan ? "/api/users/search-users?returnAll=true&roleName=PIC Registrasi" : null, getFetcher);
  const picRegistrasiOptions = useMemo(() => picRegistrasiData?.data?.map((item) => ({ value: item.id, label: item.fullName })) || [], [picRegistrasiData]);

  const { getRegistrationDetailPath } = useCustomNavigate();
  const [startDate, setStartDate] = useState(""); // for filters
  const [endDate, setEndDate] = useState("");

  // State for search queries
  const [searchQuery, setSearchQuery] = useState("");
  const [searchRiwayat, setSearchRiwayat] = useState("");

  // Pagination state - keep it simple
  const [currentPage, setCurrentPage] = useState(0); // 0-based untuk MUI DataGrid
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const [currentPageRiwayat, setCurrentPageRiwayat] = useState(0);
  const [currentPageSizeRiwayat, setCurrentPageSizeRiwayat] = useState(10);

  // Set default tanggal awal tahun dan akhir tahun
  useEffect(() => {
    const currentYearStart = format(startOfYear(new Date()), "yyyy-MM-dd");
    const currentYearEnd = format(endOfYear(new Date()), "yyyy-MM-dd");
    setStartDate(currentYearStart);
    setEndDate(currentYearEnd);
  }, []);

  // API URL yang stabil
  const apiUrl = useMemo(() => {
    const actualPage = currentPage + 1; // Convert to 1-based for API
    let baseParams = `returnAll=false&sortBy=tanggal_pengajuan&sortOrder=desc&page=${actualPage}&limit=${currentPageSize}`;

    if (searchQuery.trim()) {
      baseParams += `&search=${encodeURIComponent(searchQuery.trim())}`;
    }

    if (isDirektur) {
      return `/api/registrasi/find-all/search?status=${StatusPermohonanRegistrasi.DRAFT_SK_TANDA_TANGAN_DIREKTUR},${StatusPermohonanRegistrasi.KIRIM_INSW}&${baseParams}`;
    }

    return `/api/registrasi/find-all/search?${baseParams}`;
  }, [isDirektur, currentPage, currentPageSize, searchQuery]);

  const {
    data: draft,
    isLoading: loadingDraft,
    mutate: mutateDraft,
  } = useSWR(apiUrl, getFetcher, {
    keepPreviousData: true, // Mencegah flicker saat loading
  });

  const riwayatUrl = useMemo(() => {
    const actualPage = currentPageRiwayat + 1; // Convert to 1-based for API
    let baseParams = `status=riwayat,${StatusPermohonanRegistrasi.SELESAI},${StatusPermohonanRegistrasi.DITOLAK}&returnAll=false&startDate=${startDate}&endDate=${endDate}&page=${actualPage}&limit=${currentPageSizeRiwayat}&sortBy=tanggal_pengajuan&sortOrder=desc`;

    if (searchRiwayat.trim()) {
      baseParams += `&search=${encodeURIComponent(searchRiwayat.trim())}`;
    }

    return `/api/registrasi/find-all/search?${baseParams}`;
  }, [startDate, endDate, searchRiwayat, currentPageRiwayat, currentPageSizeRiwayat]);

  const handlePageChangeRiwayat = useCallback((newPage) => {
    console.log("Riwayat page changing to:", newPage);
    setCurrentPageRiwayat(newPage);
  }, []);

  const handlePageSizeChangeRiwayat = useCallback((newPageSize) => {
    console.log("Riwayat page size changing to:", newPageSize);
    setCurrentPageSizeRiwayat(newPageSize);
    setCurrentPageRiwayat(0); // Reset to first page
  }, []);

  const {
    data: riwayat,
    isLoading: loadingRiwayat,
    mutate: mutateRiwayat,
  } = useSWR(riwayatUrl, getFetcher, {
    keepPreviousData: true,
  });

  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0); // Reset ke halaman pertama saat search berubah
  }, []);

  const handleSearchRiwayatChange = useCallback((event) => {
    setSearchRiwayat(event.target.value);
    setCurrentPageRiwayat(0); // Reset ke halaman pertama saat search berubah
  }, []);

  // Pagination handlers yang sederhana
  const handlePageChange = useCallback((newPage) => {
    console.log("Page changing to:", newPage);
    setCurrentPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    console.log("Page size changing to:", newPageSize);
    setCurrentPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page
  }, []);

  const onSubmit = async (id) => {
    try {
      const data = {
        id: id,
        status: "riwayat",
        approval_status: "selesai",
        jnsPengajuan: "0000000010",
      };
      await sendInswPerubahan(data);
      mutateRiwayat();
      toast.success("Berhasil Non Aktif SK ke INSW");
    } catch (error) {
      const errorMessage = error.response?.data?.message?.message || "Error tidak diketahui";
      toast.error("Gagal Non Aktif SK ke INSW: " + errorMessage);
    }
  };

  const onPerubahanSubmit = async (id) => {
    try {
      const data = {
        id: id,
        status: "riwayat",
        approval_status: "selesai",
        jnsPengajuan: "0010101000",
      };
      await sendInswPerubahan(data);
      mutateRiwayat();
      toast.success("Berhasil Kirim SK ke INSW");
    } catch (error) {
      const errorMessage = error.response?.data?.message?.message || "Error tidak diketahui";
      toast.error("Gagal Kirim SK ke INSW: " + errorMessage);
    }
  };

  const handleDelegasiChange = useCallback(
    async (id, delegasiUserId) => {
      try {
        await patchFetcherWithoutId(`/api/registrasi/${id}/delegasi`, { delegasiUserId });
        toast.success("Delegasi berhasil diperbarui");
        mutateDraft();
        mutateRiwayat();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Gagal memperbarui delegasi");
      }
    },
    [mutateDraft, mutateRiwayat]
  );

  const renderDelegasiCell = useCallback(
    (params) => {
      if (!canDelegasikan) {
        return params?.row?.delegasiUser?.fullName || "-";
      }
      const currentValue = params?.row?.delegasiUser ? { value: params.row.delegasiUser.id, label: params.row.delegasiUser.fullName } : null;
      return (
        <ReactSelect
          className="w-full"
          placeholder="Pilih PIC..."
          isClearable
          value={currentValue}
          options={picRegistrasiOptions}
          onChange={(option) => handleDelegasiChange(params.row.id, option?.value ?? null)}
          /*
           * Daftar PIC digambar di <body>, bukan di dalam sel tabel.
           *
           * Sebelumnya memakai menuPosition="fixed" saja. Itu tidak cukup:
           * DataGrid memasang transform pada lapisan penggulungnya, dan elemen
           * ber-position:fixed di dalam ancestor bertransform tetap terikat pada
           * ancestor itu - jadi daftarnya ikut terpotong oleh tabel. Portal ke
           * body melepaskannya sepenuhnya dari pemotongan tersebut.
           */
          menuPortalTarget={sasaranPortalMenu()}
          menuPlacement="auto"
          styles={gayaSelectDalamTabel}
        />
      );
    },
    [canDelegasikan, picRegistrasiOptions, handleDelegasiChange]
  );

  const columns = useMemo(
    () => [
      { field: "index", headerName: "No" },
      { field: "nomor", headerName: "Nomor Registrasi" },
      { field: "name", headerName: "Perusahaan", valueGetter: (value, row) => row?.company?.name },
      {
        field: "brand",
        headerName: "Sub Layanan",
        valueGetter: (value, row) => {
          let subLayanan = row?.sub_layanan;
          if (subLayanan === "119") {
            return "Registrasi B3 (Produsen)";
          } else {
            let kategoriBahan = row?.BahanB3Registrasi?.[0]?.kategori_b3;
            return `Registrasi B3 ${kategoriBahan === "Dapat Dipergunakan" ? "(Baru)" : kategoriBahan === "Terbatas Dipergunakan" ? "(Terbatas)" : ""}`;
          }
        },
      },
      { field: "tanggal_pengajuan", headerName: "Tanggal Masuk Data", valueGetter: (value, row) => formattedDate(value) },
      {
        field: "lama_proses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          const lamaProses = params.row.lama_proses || 0;
          return lamaProses < 2 ? (
            <Chip color="success" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          ) : (
            <Chip color="danger" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="faded" className="capitalize">
            {params.value}
          </Chip>
        ),
      },
      {
        field: "delegasi",
        headerName: "Delegasi",
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: renderDelegasiCell,
      },
      {
        field: "id",
        headerName: "Aksi",
        renderCell: (params) => {
          return (
            <div className="flex gap-2">
              <Button size="sm" color="primary" isIconOnly onClick={() => getRegistrationDetailPath(params.value)}>
                <EyeIcon className="size-4" />
              </Button>
            </div>
          );
        },
      },
    ].filter((col) => !(hideAksiForKoordinator && col.headerName === "Aksi")),
    [getRegistrationDetailPath, renderDelegasiCell, hideAksiForKoordinator]
  );

  const columnsRiwayat = useMemo(
    () => [
      { field: "index", headerName: "No" },
      { field: "nomor", headerName: "Nomor Registrasi" },
      { field: "name", headerName: "Perusahaan", valueGetter: (value, row) => row?.company?.name },
      { field: "nomorSurat", headerName: "Nomor Surat", valueGetter: (value, row) => row?.nomor_surat_formatted },
      {
        field: "brand",
        headerName: "Sub Layanan",
        valueGetter: (value, row) => {
          let subLayanan = row?.sub_layanan;
          if (subLayanan === "119") {
            return "Registrasi B3 (Produsen)";
          } else {
            let kategoriBahan = row?.BahanB3Registrasi?.[0]?.kategori_b3;
            return `Registrasi B3 ${kategoriBahan === "Dapat Dipergunakan" ? "(Baru)" : kategoriBahan === "Terbatas Dipergunakan" ? "(Terbatas)" : ""}`;
          }
        },
      },
      { field: "status_izin", headerName: "Status Izin", renderCell: (params) => params?.value ?? "-" },
      // { field: "translatedAsalNegara", headerName: "Asal Negara", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedAsalNegara },
      // {
      //   field: "negaraMuat",
      //   headerName: "Negara Muat",
      //   valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedNegaraMuat,
      //   renderCell: (params) => params?.row?.BahanB3Registrasi[0]?.translatedNegaraMuat,
      // },
      // {
      //   field: "pelabuhanBongkar",
      //   headerName: "Pelabuhan Bongkar",
      //   width: 200,
      //   valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedPelabuhan,
      //   renderCell: (params) => params?.row?.BahanB3Registrasi[0]?.translatedPelabuhan,
      // },
      {
        field: "type_registrasi",
        headerName: "Tipe Registrasi",
        renderCell: (params) => params?.value ?? "-",
      },
      {
        field: "tanggal_pengajuan",
        headerName: "Tanggal Masuk Data",
        width: 200,
        valueGetter: (value, row) => {
          return formattedDate(value);
        },
      },
      {
        field: "lama_proses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          const lamaProses = params.row.lama_proses || 0;
          return lamaProses < 2 ? (
            <Chip color="success" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          ) : (
            <Chip color="danger" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="faded" className="capitalize">
            {params.value}
          </Chip>
        ),
      },
      {
        field: "delegasi",
        headerName: "Delegasi",
        valueGetter: (value, row) => row?.delegasiUser?.fullName || "-",
      },
      {
        field: "berlaku_dari",
        headerName: "Berlaku Dari",
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "berlaku_sampai",
        headerName: "Berlaku Sampai",
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "file_surat",
        headerName: "File SK",
        renderCell: (params) => {
          if (!params?.row?.file_surat) {
            return <FormAddSK id={params?.row?.id} mutate={mutateRiwayat} />;
          } else {
            const buktiUrl = `${BASE_URL}/${params?.row?.file_surat}`;
            return (
              <a href={buktiUrl} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
                Lihat File SK
              </a>
            );
          }
        },
      },
      {
        field: "is_insw",
        headerName: "INSW Status",
        valueGetter: (value, row) => (row?.tipe_registrasi === "produsen" || row?.sub_layanan == "119" ? "-" : row?.is_insw === true ? "Sudah Kirim INSW" : "Belum Kirim atau DiNonaktifkan INSW"),
        renderCell: (params) =>
          params?.prow?.tipe_registrasi === "produsen" || params?.row?.sub_layanan == "119" ? (
            "-"
          ) : params?.row?.is_insw === true ? (
            <Chip size="sm" color="success" variant="flat">
              Sudah Kirim INSW
            </Chip>
          ) : (
            <Chip size="sm" color="warning" variant="flat">
              Belum Kirim atau DiNonaktifkan INSW
            </Chip>
          ),
      },

      {
        field: "tanggal_pencabutan_insw",
        headerName: "Tanggal Pencabutan INSW",
        renderCell: (params) => {
          const tanggalPerubahan = params?.row?.tanggal_pencabutan_insw;

          if (tanggalPerubahan) {
            const formattedTanggal = formattedDate(tanggalPerubahan);
            return `${formattedTanggal}`;
          }

          return "-";
        },
      },

      {
        field: "id",
        headerName: "Aksi",
        width: 400,
        renderCell: (params) => (
          <div className={"flex gap-2"}>
            <Button size="sm" color="primary" isIconOnly onClick={() => getRegistrationDetailPath(params.value)}>
              <EyeIcon className="size-4" />
            </Button>
            {params?.row?.sub_layanan !== "119" ? (
              <>
                <Button startContent={<PaperAirplaneIcon className="size-4" />} size="sm" color="warning" onPress={() => onPerubahanSubmit(params.value)}>
                  Kirim Ulang SK INSW
                </Button>
                <Button startContent={<PaperAirplaneIcon className="size-4" />} size="sm" color="danger" onPress={() => onSubmit(params.value)}>
                  Non Aktif SK INSW
                </Button>
              </>
            ) : null}
          </div>
        ),
      },
    ].filter((col) => !(hideAksiForKoordinator && col.headerName === "Aksi")),
    [getRegistrationDetailPath, mutateRiwayat, hideAksiForKoordinator]
  );

  // handles for the filter component
  const handleStartDateChange = useCallback((event) => {
    setStartDate(event.target.value);
    setCurrentPageRiwayat(0); // Reset pagination saat filter berubah
  }, []);

  const handleEndDateChange = useCallback((event) => {
    setEndDate(event.target.value);
    setCurrentPageRiwayat(0); // Reset pagination saat filter berubah
  }, []);

  async function onValidateGetPermohonan() {
    try {
      setIsLoadingPtpsp(true);

      const data = {
        status: StatusPermohonanRegistrasi.PEMBUATAN_DRAFT_SK,
        subLayanan: ["1", "2"],
      };
      await postFetcher(`/api/registrasi/get-from-ptsp-admin`, data);

      // Reset pagination ke halaman pertama setelah update data
      setCurrentPage(0);

      mutateDraft();
      mutateRiwayat();
      toast.success("Berhasil Update List Permohonan Registrasi Dari PTSP!");
      setIsLoadingPtpsp(false);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        isResponseErrorObject(error.response.data.message)
          ? Object.entries(error.response.data.message).forEach(([key, value]) => {
              toast.error(value);
            })
          : toast.error(error.response.data.message);
      } else {
        console.log(error);
        toast.error("An unexpected error occurred.");
      }
      mutateDraft();
      mutateRiwayat();
      setIsLoadingPtpsp(false);
    }
  }

  const handleRefreshData = useCallback(() => {
    mutateDraft();
    mutateRiwayat();
  }, [mutateDraft, mutateRiwayat]);

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>Daftar Permohonan Registrasi</CardHeader>
        <Divider />
        <CardBody className="w-full">
          {/* <div className="pb-3">
            <Button isLoading={isLoadingPtpsp} onClick={onValidateGetPermohonan} size="sm" color="primary" startContent={<ArrowPathIcon className="size-4 stroke-2" />}>
              Update Permohonan Registrasi Dari PTSP
            </Button>
            <Button size="sm" color="primary" isLoading={loadingRiwayat || loadingDraft} onClick={handleRefreshData} startContent={<ArrowPathIcon className="size-4 stroke-2" />}>
              Refresh Data
            </Button>
          </div> */}

          <Tabs
            color="primary"
            variant="underlined"
            aria-label="tabs"
            classNames={{
              tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
              cursor: "w-full",
              tab: "max-w-fit px-1.5 h-12",
              tabContent: "group-data-[selected=true]:font-semibold",
            }}
          >
            <Tab title="Draft SK" key="draftSk">
              <div className="mb-4 w-full">
                <SearchField value={searchQuery} onChange={handleSearchChange} placeholder="Cari no. registrasi / nama perusahaan" />
              </div>
              <div className="w-full h-[550px]">
                <ClientDataGrid
                  columns={columns}
                  rows={draft?.registrasi || []}
                  isLoading={loadingDraft}
                  rowCount={draft?.total || 0}
                  pagination
                  page={currentPage}
                  pageSize={currentPageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  paginationMode="server"
                />
              </div>
            </Tab>
            <Tab title="Riwayat" key="riwayat">
              <Filter startDate={startDate} handleStartDate={handleStartDateChange} endDate={endDate} handleEndDate={handleEndDateChange} />
              {/* <div className="mt-4 mb-4">
                <SearchField value={searchRiwayat} onChange={handleSearchRiwayatChange} placeholder="Cari nomor registrasi, nama perusahaan, nomor surat..." />
              </div> */}
              <div className="w-full h-[550px]">
                <ClientDataGrid
                  columns={columnsRiwayat}
                  rows={riwayat?.registrasi || []}
                  isLoading={loadingRiwayat}
                  rowCount={riwayat?.total || 0} // Tambahkan rowCount
                  pagination // Tambahkan pagination
                  page={currentPageRiwayat} // Tambahkan page
                  pageSize={currentPageSizeRiwayat} // Tambahkan pageSize
                  onPageChange={handlePageChangeRiwayat} // Tambahkan onPageChange
                  onPageSizeChange={handlePageSizeChangeRiwayat} // Tambahkan onPageSizeChange
                  paginationMode="server" // Tambahkan paginationMode
                />
              </div>
            </Tab>
          </Tabs>
        </CardBody>
      </Card>
    </RootAdmin>
  );
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <div className="w-full">
      <div className="w-80 sm:w-2/5">
        <input
          type="text"
          id="search"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-3 py-1 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

function Filter({ startDate, handleStartDate, endDate, handleEndDate }) {
  return (
    <div className="flex flex-wrap gap-4">
      <div className="flex flex-col gap-1">
        <label htmlFor="start-date">Tanggal Mulai</label>
        <input type="date" id="start-date" value={startDate} onChange={handleStartDate} className="w-full" />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="end-date">Tanggal Akhir</label>
        <input type="date" id="end-date" value={endDate} onChange={handleEndDate} className="w-full" />
      </div>
    </div>
  );
}
